<template>
  <div class="size-full flex flex-col">
    <ScrollArea class="flex-1">
      <router-view />
    </ScrollArea>
    <div class="flex gap-4 justify-center my-4">
      <Button
        v-for="tab in showTabs"
        :key="tab.path"
        color="glass"
        size="lg"
        :active="isActive(tab.path)"
        @click="handleTabClick(tab)"
      >
        <Icon :icon="tab.icon" />
        {{ $t(tab.name) }}
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { Icon, Button, ScrollArea } from '@rapoo/element'
  import type { KeyboardPerfCapability } from '@rapoo/device-sdk'
  import { useDeviceStore } from '@/stores/device'
  import { useAdminOnly } from '@/composables/useAdminOnly'
  import { storeToRefs } from 'pinia'
  import type { MousePerfCapability, MouseRTCapability } from '@rapoo/device-sdk'

  /**
   * Tab 配置项接口
   */
  interface TabConfig {
    /** Tab 显示名称 */
    name: string
    /** 图标名称 */
    icon: string
    /** 路由路径片段 */
    path: string
  }

  // 路由与状态管理
  const router = useRouter()
  const route = useRoute()
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  /**
   * Tab 配置映射表
   * 定义不同设备类型对应的功能 Tab 列表
   */
  const TABS_CONFIG: Record<string, TabConfig[]> = {
    keyboard: [
      { name: 'common.footerTab.lighting', icon: 'Status-Wireframe-Day', path: 'light' },
      {
        name: 'common.footerTab.keyboardPerformanceSettings',
        icon: 'Tab-Wireframe-PerfMode',
        path: 'performance-settings',
      },
      { name: 'common.footerTab.itinerary', icon: 'Tab-Wireframe-Stroke', path: 'itinerary' },
      { name: 'common.footerTab.keys', icon: 'Hardware-Wireframe-Keyboard', path: 'keys' },
      {
        name: 'common.footerTab.keyboardSettings',
        icon: 'Tab-Wireframe-DeviceSetting',
        path: 'settings',
      },
    ],
    mouse: [
      { name: 'common.footerTab.dpi', icon: 'Tab-Wireframe-DPI', path: 'dpi' },
      { name: 'common.footerTab.keys', icon: 'Tab-Wireframe-Key', path: 'keys' },
      { name: 'common.footerTab.performance', icon: 'Tab-Wireframe-PerfMode', path: 'performance' },
      { name: 'common.footerTab.rt', icon: 'Function-Wireframe-CurrentClick', path: 'rt' },
      {
        name: 'common.footerTab.parameters',
        icon: 'Tab-Wireframe-AudioEffect',
        path: 'parameters',
      },
      {
        name: 'common.footerTab.mouseSettings',
        icon: 'Tab-Wireframe-DeviceSetting',
        path: 'settings',
      },
    ],
    base: [
      { name: 'common.footerTab.pages', icon: 'Tab-Wireframe-Device', path: 'pages' },
      { name: 'common.footerTab.playback', icon: 'Multimedia-Wireframe-Player', path: 'player' },
      { name: 'common.footerTab.lighting', icon: 'Tab-Wireframe-Lighting', path: 'light' },
      {
        name: 'common.footerTab.baseSettings',
        icon: 'Tab-Wireframe-DeviceSetting',
        path: 'settings',
      },
    ],
  }

  /**
   * 计算当前应该显示的 Tab 列表
   * 优先使用 currentDevice 中的 manifest 信息，如果未选中设备则回退到路由元数据
   */
  const { canShowBaseScreenContent, canShowKeyboardItinerary, canShowKeyboardKeys } = useAdminOnly()

  const canShowKeyboardPerformanceSettings = computed(() => {
    const keyboardPerf = currentDevice.value?.manifest.capabilities?.find(
      (capability): capability is KeyboardPerfCapability => capability.type === 'keyboardPerf',
    )
    return keyboardPerf?.config.enablePerformanceConfig === true
  })

  const showTabs = computed(() => {
    // 这里体现了 manifest 的核心用途：驱动 UI 层的动态渲染
    const type = currentDevice.value?.manifest.type || (route.meta.type as string) || 'keyboard'
    const tabs = TABS_CONFIG[type] || TABS_CONFIG.keyboard
    // base 类型：pages 和 player 需要满足 admin-only 条件才显示
    if (type === 'base') {
      const hasScreen = currentDevice.value?.manifest.capabilities?.some(
        (c) => c.type === 'baseScreen' && c.config.screenType === 'screen',
      )
      if (!hasScreen) return tabs.filter((t) => t.path !== 'pages' && t.path !== 'player')
      // 网吧渠道即使有屏幕，也需要管理员权限才能看到 pages 和 player
      if (!canShowBaseScreenContent.value)
        return tabs.filter((t) => t.path !== 'pages' && t.path !== 'player')
    }
    // keyboard：行程 / 改键由 useAdminOnly 统一判断（含网吧管理员与设备能力）
    if (type === 'keyboard') {
      return tabs.filter((t) => {
        if (t.path === 'itinerary') return canShowKeyboardItinerary.value
        if (t.path === 'keys') return canShowKeyboardKeys.value
        if (t.path === 'performance-settings') return canShowKeyboardPerformanceSettings.value
        return true
      })
    }

    if (type === 'mouse') {
      const mouseRTCap = currentDevice.value?.manifest.capabilities?.find(
        (c): c is MouseRTCapability => c.type === 'mouse-rt',
      )
      const mousePerfCap = currentDevice.value?.manifest.capabilities?.find(
        (c): c is MousePerfCapability => c.type === 'mousePerf',
      )
      return tabs.filter((t) => {
        if (t.path === 'rt') return mouseRTCap?.config.enable === true
        if (t.path === 'performance') return mousePerfCap?.config.enable === true
        return true
      })
    }

    return tabs
  })

  /**
   * 处理 Tab 点击事件
   * 跳转到对应的设备类型子路由
   * @param tab 被点击的 Tab 配置对象
   */
  const handleTabClick = (tab: TabConfig) => {
    const type = currentDevice.value?.manifest.type || (route.meta.type as string) || 'keyboard'
    router.push(`/device/${type}/${tab.path}`)
  }

  /**
   * 判断 Tab 是否处于激活状态
   * @param path Tab 的路径片段
   */
  const isActive = (path: string) => {
    return route.path.endsWith('/' + path)
  }
</script>

<style scoped>
  :deep(div[data-reka-scroll-area-viewport] > div:first-child) {
    height: 100%;
  }
</style>
